import type { Block } from "building-types/blocks/blockTypes";
import {
  ComponentVerticalPlacementType,
  type ComponentInstance,
} from "building-types/components/componentInstanceTypes";
import {
  type ComponentTransformVerticalPlacement,
  type ComponentTransform,
  createComponentTransformVerticalPlacementRemapping,
  createComponentTransformVerticalPlacementReplication,
} from "building-types/components/componentTypes";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import { DatumVerticalUsageType } from "building-types/datums/datumUsageTypes";
import type { DocForeignHashJoinTable } from "building-types/doc/docForeignKeyTable";
import type { ComponentOwnedMemberEntityType } from "building-types/handles/docEntityHandleTypes";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { OptionDefinitions } from "building-types/options/optionDefinitions";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { RWTable, Table } from "common/data/table";
import type { WorldInches } from "common/types/nominalNumbersTypes";
import { arrayShift } from "common/utils/arrayShift";
import { worldInchesNew } from "common/utils/nominalNumbers";
import { buildComponentSourceLevels } from "doc/docComponentSourceLevels";
import { instanceIdForComponent } from "doc/entity/componentInstanceEntityResolve";
import { parseEntityInfoFromHandleHash } from "doc/entity/docEntityHandleHash";
import { ComponentCircularDependencyErrorNotification } from "doc/entity/docEntityTransactErrorNotification";
import { evaluateEntityForComponentInstance } from "doc/formula/evalFormulas";
import { WindingDir } from "geom/core";
import type { Mat3 } from "geom/mat3";
import { mat3Ident, mat3Inverse, mat3Mult, mat3Scale, mat3Translate } from "geom/mat3";
import {
  polyLine2Copy,
  polyLine2IsClosed,
  polyLine2Reverse,
  polyLine2Shift,
  polyLine2Transform,
  polyLine2WindingDir,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";

export function buildComponentInstanceVerticalOffset(
  instance: ComponentInstance,
  datumAbstractions: Table<DatumAbstraction>,
): WorldInches {
  const { verticalPlacement } = instance;
  const { type } = verticalPlacement;
  if (type !== ComponentVerticalPlacementType.REPLICATION || !verticalPlacement.datumUsage) {
    return worldInchesNew(0);
  }

  const { entityId: datumId, offset: extraOffset = 0 } = verticalPlacement.datumUsage;
  if (!datumId) {
    return worldInchesNew(0);
  }

  const datum = datumAbstractions[datumId];
  if (!datum?.isValidForUsage(DatumVerticalUsageType.COMPONENT_PLACEMENT)) {
    return worldInchesNew(0);
  }

  return datum.resolveVerticalDatum({
    extraOffset,
  });
}

export function buildComponentTransformVerticalPlacement(
  instance: ComponentInstance,
  datumAbstractions: Table<DatumAbstraction>,
): ComponentTransformVerticalPlacement {
  const { verticalPlacement } = instance;
  const { type } = verticalPlacement;
  if (type === ComponentVerticalPlacementType.REMAPPING) {
    return createComponentTransformVerticalPlacementRemapping(verticalPlacement.levelRemapping);
  }

  const offset = buildComponentInstanceVerticalOffset(instance, datumAbstractions);

  return createComponentTransformVerticalPlacementReplication(offset);
}

export function buildComponentInstanceTransformMat3(instance: ComponentInstance): Mat3 {
  let transform = mat3Ident();

  // Apply mirroring.
  const mirrorX = instance?.mirrorX ?? false;
  const mirrorY = instance?.mirrorY ?? false;
  const mirrorCenterX = instance?.mirrorCenterX ?? 0;
  const mirrorCenterY = instance?.mirrorCenterY ?? 0;

  if (mirrorX || mirrorY) {
    const translate1 = mat3Translate(-mirrorCenterX, -mirrorCenterY);
    const scale = mat3Scale(mirrorX ? -1 : 1, mirrorY ? -1 : 1);
    const translate2 = mat3Translate(mirrorCenterX, mirrorCenterY);
    transform = mat3Mult(translate1, transform);
    transform = mat3Mult(scale, transform);
    transform = mat3Mult(translate2, transform);
  }

  // Apply component instance matrix.
  transform = mat3Mult(instance.matrix, transform);

  return transform;
}

/**
 * Returns a matrix for transforming the component to an instance, accounting for the global mirror.
 * Since the global mirror affects components, it must un-mirror, apply the transform, and then re-mirror
 */
export function componentInstanceMat3ForMirroredTemplate(
  mirror: boolean,
  mirrorTranslationX: number,
  instanceMat: Mat3,
): Mat3 {
  if (!mirror) {
    return instanceMat;
  }
  const M = mat3Mult(mat3Translate(mirrorTranslationX, 0), mat3Scale(-1, 1));
  return mat3Mult(M, mat3Mult(instanceMat, M));
}

export function applyTransformToOutline(transform: Mat3, outline: PolyLine2): PolyLine2 {
  return polyLine2Transform(polyLine2Copy(outline), transform);
}

/**
 * Applies component instance transforms to an outline and edgeIds.
 * Handles orientation flipping and winding direction correction.
 */
export function applyTransformToOutlineAndEdgeIds(
  transform: ComponentTransform,
  outline: PolyLine2,
  edgeIds: string[],
): { transformedOutline: PolyLine2; transformedEdgeIds: string[] } {
  const transformedOutline = applyTransformToOutline(transform.matrix, outline);
  let transformedEdgeIds = edgeIds.slice();

  const reverseOutline = () => {
    const closed = polyLine2IsClosed(transformedOutline);
    polyLine2Reverse(transformedOutline);
    if (closed) {
      polyLine2Shift(transformedOutline, 1);
      transformedEdgeIds = arrayShift(transformedEdgeIds.slice().reverse(), 1);
    } else {
      transformedEdgeIds = transformedEdgeIds.slice().reverse();
    }
  };

  if (transform.reverseOrientation) {
    reverseOutline();
  }
  // Ensure CCW winding for downstream assumptions; if we flip winding, also update edgeIds.
  if (polyLine2WindingDir(transformedOutline) !== WindingDir.CCW) {
    reverseOutline();
  }

  return { transformedOutline, transformedEdgeIds };
}

interface BuildComponentTransformsProps {
  readonly rooms: Table<StaticRoom>;
  readonly blocks: Table<Block>;
  readonly componentMembers: DocForeignHashJoinTable<ComponentOwnedMemberEntityType, ForeignEntityId<"COMPONENT">>;
  readonly evaluatedComponentInstances: Table<ComponentInstance>;
  readonly componentInstances: Table<ComponentInstance>;
  readonly datumAbstractions: Table<DatumAbstraction>;
  readonly definitions: OptionDefinitions;
  readonly optionChoices: OptionChoices;
}

interface BuildComponentTransformsResult {
  readonly idToParents: Table<string[]>;
  readonly componentToTransforms: Table<ComponentTransform[]>;
}

export function buildComponentTransforms({
  rooms,
  blocks,
  componentMembers,
  evaluatedComponentInstances,
  componentInstances,
  datumAbstractions,
  definitions,
  optionChoices,
}: BuildComponentTransformsProps): BuildComponentTransformsResult {
  // Build reverse lookups for component members to support nested components.
  const idToParents: RWTable<string[]> = {};
  for (const hash in componentMembers) {
    const componentId = componentMembers[hash];
    const entityInfo = parseEntityInfoFromHandleHash(hash);
    if (entityInfo && componentId) {
      idToParents[entityInfo.entityId] ||= [];
      idToParents[entityInfo.entityId].push(componentId);
    }
  }

  const componentToInstances: RWTable<string[]> = {};
  for (const instanceId in evaluatedComponentInstances) {
    const instance = evaluatedComponentInstances[instanceId];
    const componentId = instance.componentId;
    componentToInstances[componentId] ||= [];
    componentToInstances[componentId].push(instanceId);
  }

  const instanceTransforms: RWTable<ComponentTransform> = {};
  for (const instanceId in evaluatedComponentInstances) {
    const componentInstance = evaluatedComponentInstances[instanceId];
    const matrix = buildComponentInstanceTransformMat3(componentInstance);
    instanceTransforms[instanceId] = {
      instanceId,
      sourceLevels: buildComponentSourceLevels({
        componentInstance,
        componentMembers,
        componentInstances,
        rooms,
        blocks,
      }),
      componentInstanceChain: [instanceId],
      matrix,
      inverseMatrix: mat3Inverse(matrix),
      verticalPlacement: buildComponentTransformVerticalPlacement(componentInstance, datumAbstractions),
      reverseOrientation: (componentInstance.mirrorX ?? false) !== (componentInstance.mirrorY ?? false),
      mirrorX: componentInstance.mirrorX ?? false,
      mirrorY: componentInstance.mirrorY ?? false,
    };
  }

  // Pre-compute disabled states for nested instances to avoid expensive evaluation in nested loops
  // This is a lookup table of instanceId -> containingComponentInstanceId -> disabled
  const disabledInContext: Map<string, Map<string, boolean>> = new Map();
  for (const instanceId in componentInstances) {
    const parentComponentIds = idToParents[instanceId] || [];
    if (parentComponentIds.length === 0) {
      continue;
    }

    const instanceMap = new Map<string, boolean>();
    for (const parentComponentId of parentComponentIds) {
      const parentInstanceIds = componentToInstances[parentComponentId] || [];
      for (const parentInstanceId of parentInstanceIds) {
        const evaluated = evaluateEntityForComponentInstance(
          componentInstances[instanceId],
          [parentInstanceId],
          definitions,
          optionChoices,
        );
        // After evaluation, disabled should be a boolean (formulas are evaluated)
        const isDisabled = evaluated.disabled === true;
        instanceMap.set(parentInstanceId, isDisabled);
      }
    }
    disabledInContext.set(instanceId, instanceMap);
  }

  const componentToTransforms: RWTable<ComponentTransform[]> = {};
  for (const componentId in componentToInstances) {
    calculateComponentToTransforms(
      componentId,
      componentToInstances,
      idToParents,
      instanceTransforms,
      [],
      componentToTransforms,
      evaluatedComponentInstances,
      disabledInContext,
    );
  }

  return { idToParents, componentToTransforms };
}

// For a given component, calculate the transforms of all its instances.
function calculateComponentToTransforms(
  componentId: string,
  componentToInstances: RWTable<string[]>,
  idToParents: RWTable<string[]>,
  instanceTransforms: RWTable<ComponentTransform>,
  componentStack: string[],
  componentToTransforms: RWTable<ComponentTransform[]>,
  evaluatedComponentInstances: Table<ComponentInstance>,
  disabledInContext: Map<string, Map<string, boolean>>,
): void {
  // Return if the value has already been calculated.
  if (componentToTransforms[componentId]) {
    return;
  }

  if (componentStack.includes(componentId)) {
    throw new ComponentCircularDependencyErrorNotification(
      "Circular dependency detected. A component is nested inside itself: " +
        componentStack.join(" -> ") +
        " -> " +
        componentId,
    );
  }

  // Calculate componentToTransforms for any nested component that this relies on.
  const instanceIds = componentToInstances[componentId] || [];
  for (const instanceId of instanceIds) {
    const parentIds = idToParents[instanceId] || [];
    for (const parentId of parentIds) {
      calculateComponentToTransforms(
        parentId,
        componentToInstances,
        idToParents,
        instanceTransforms,
        [...componentStack, componentId],
        componentToTransforms,
        evaluatedComponentInstances,
        disabledInContext,
      );
    }
  }

  // Calculate all transforms of the given component.
  componentToTransforms[componentId] = [];
  for (const instanceId of instanceIds) {
    // Add the normal base transform.
    const baseTransform = instanceTransforms[instanceId];
    const parentIds = idToParents[instanceId] || [];
    // Only add base transform if NOT disabled globally
    if (!evaluatedComponentInstances[instanceId].disabled) {
      componentToTransforms[componentId].push(baseTransform);
    }
    // For each component that the instance is nested in, add all the transforms of the containing component instance.
    for (const parentId of parentIds) {
      const parentTransforms = componentToTransforms[parentId];
      for (const parentTransform of parentTransforms) {
        // Get the containing component instance ID from the parent transform chain
        // Note, this is containment *in the Building* not in the Doc. In the Doc,
        // component instances cannot host other component instances. But when we generate the building,
        // the nesting is expanded to a conceptual hosting chain of component instances.
        const componentInstanceChain = parentTransform.componentInstanceChain;
        const containingComponentInstanceId = componentInstanceChain[componentInstanceChain.length - 1];

        // Check if disabled in the parent instance's context using pre-computed lookup
        const disabled = disabledInContext.get(instanceId)?.get(containingComponentInstanceId) ?? false;
        if (disabled) {
          continue; // Skip this transform - disabled in this context
        }

        componentToTransforms[componentId].push(mergeComponentTransforms(baseTransform, parentTransform));
      }
    }
  }
}

// Merge two component transforms.
function mergeComponentTransforms(
  baseTransform: ComponentTransform,
  parentTransform: ComponentTransform,
): ComponentTransform {
  const matrix = mat3Mult(parentTransform.matrix, baseTransform.matrix);
  return {
    instanceId: parentTransform.instanceId,
    componentInstanceChain: [...parentTransform.componentInstanceChain, baseTransform.instanceId],
    matrix,
    inverseMatrix: mat3Inverse(matrix),
    sourceLevels: new Set([...parentTransform.sourceLevels, ...baseTransform.sourceLevels]),
    verticalPlacement: mergeComponentTransformVerticalPlacements(
      baseTransform.verticalPlacement,
      parentTransform.verticalPlacement,
      parentTransform.componentInstanceChain,
    ),
    reverseOrientation: baseTransform.reverseOrientation !== parentTransform.reverseOrientation,
    mirrorX: (baseTransform.mirrorX ?? false) !== (parentTransform.mirrorX ?? false),
    mirrorY: (baseTransform.mirrorY ?? false) !== (parentTransform.mirrorY ?? false),
  };
}

function mergeComponentTransformVerticalPlacements(
  baseTransformVerticalPlacement: ComponentTransformVerticalPlacement,
  parentTransformVerticalPlacement: ComponentTransformVerticalPlacement,
  instanceChain: string[],
): ComponentTransformVerticalPlacement {
  if (
    baseTransformVerticalPlacement.type === ComponentVerticalPlacementType.REPLICATION &&
    parentTransformVerticalPlacement.type === ComponentVerticalPlacementType.REPLICATION
  ) {
    return createComponentTransformVerticalPlacementReplication(
      baseTransformVerticalPlacement.offset + parentTransformVerticalPlacement.offset,
    );
  } else if (
    baseTransformVerticalPlacement.type === ComponentVerticalPlacementType.REMAPPING &&
    parentTransformVerticalPlacement.type === ComponentVerticalPlacementType.REMAPPING
  ) {
    // Compose level remappings: if base maps A→B and parent maps B→C, result should map A→C
    const composedRemapping: Record<string, string> = {};
    for (const sourceLevel in baseTransformVerticalPlacement.levelRemapping) {
      const intermediateLevel = baseTransformVerticalPlacement.levelRemapping[sourceLevel] ?? sourceLevel;
      // Follow the chain: look up the intermediate level in the parent's remapping
      const finalLevel = parentTransformVerticalPlacement.levelRemapping[intermediateLevel] ?? intermediateLevel;
      composedRemapping[sourceLevel] = finalLevel;
    }
    return createComponentTransformVerticalPlacementRemapping(composedRemapping);
  } else if (
    baseTransformVerticalPlacement.type === ComponentVerticalPlacementType.REPLICATION &&
    parentTransformVerticalPlacement.type === ComponentVerticalPlacementType.REMAPPING
  ) {
    return createComponentTransformVerticalPlacementReplication(baseTransformVerticalPlacement.offset);
  } else if (
    baseTransformVerticalPlacement.type === ComponentVerticalPlacementType.REMAPPING &&
    parentTransformVerticalPlacement.type === ComponentVerticalPlacementType.REPLICATION
  ) {
    // remap to the corresponding ephemeral level from the instance chain
    const composedRemapping: Record<string, string> = {};
    for (const sourceLevel in baseTransformVerticalPlacement.levelRemapping) {
      composedRemapping[sourceLevel] = instanceIdForComponent(
        baseTransformVerticalPlacement.levelRemapping[sourceLevel] ?? sourceLevel,
        instanceChain,
      );
    }

    return createComponentTransformVerticalPlacementRemapping(composedRemapping);
  } else {
    throw new Error("Invalid vertical placement type combination");
  }
}
